import { Button, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@mui/material';
import { DatePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { IconCheck, IconPlus, IconTrash } from '@tabler/icons-react';
import axios from 'axios';
import dayjs from 'dayjs';
import React, { useState, useRef } from 'react';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import ReactSelect from 'react-select';
import { useParams } from 'react-router-dom';
import { Toast } from 'primereact/toast';


export default function SubShiftAdd() {
    const toast = useRef(null);
    const { id } = useParams();
    const [shiftName, setShiftName] = useState("");
    const [shiftType, setShiftType] = useState("");
    const [priority, setPriority] = useState(1);
    const [cutOffTime, setCutOffTime] = useState(dayjs());
    const [cutOffLeaveType, setCutOffLeaveType] = useState("");
    const [startTime, setStartTime] = useState(dayjs());
    const [endTime, setEndTime] = useState(dayjs());
    const [workingHours, setWorkingHours] = useState(1);
    const [effectiveFrom, setEffectiveFrom] = useState(dayjs());
    const [breaks, setBreaks] = useState([]);
    const [repeatDays, setRepeatDays] = useState(false);
    const [selectedRepeatDays, setSelectedRepeatDays] = useState([]);
    const [specificDate, setSpecificDate] = useState(dayjs());
    const [transitionTime, setTransitionTime] = useState(dayjs("1970-01-01T00:00:00"));

    const [newBreak, setNewBreak] = useState({
        name: "",
        type: "",
        break_duration: 1,
        startTime: dayjs(),
        endTime: dayjs(),
    });

    const repeatOptions = [
        { value: "Monday", label: "Monday" },
        { value: "Tuesday", label: "Tuesday" },
        { value: "Wednesday", label: "Wednesday" },
        { value: "Thursday", label: "Thursday" },
        { value: "Friday", label: "Friday" },
        { value: "Saturday", label: "Saturday" },
        { value: "Sunday", label: "Sunday" },
        { value: "First Saturday", label: "First Saturday" },
        { value: "Second Saturday", label: "Second Saturday" },
        { value: "Third Saturday", label: "Third Saturday" },
        { value: "Fourth Saturday", label: "Fourth Saturday" },
    ];

    const handleAddBreak = () => {
        if (newBreak.name && newBreak.type) {
            if (newBreak.type === "Flexible" && newBreak.break_duration <= 0) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Break Duration must be a positive value.',
                    life: 5000,
                });
                return;
            }
            setBreaks([...breaks, newBreak]);
            setNewBreak({
                name: "",
                type: "",
                break_duration: 1,
                startTime: dayjs(),
                endTime: dayjs(),
            });
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Break added successfully!',
                life: 5000,
            });
            document.getElementById("closeBreakModal").click(); // Close modal
        } else {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please complete the break details before adding.',
                life: 5000,
            });
        }
    };

    const formatDuration = (hours) =>
        dayjs.duration(hours, "hours").format("HH:mm:ss");

    const handleRemoveBreak = (index) => {
        setBreaks(breaks.filter((_, i) => i !== index));
    };

    const calculateDuration = (start, end) => {
        const diff = dayjs.duration(end.diff(start));
        return `${diff.hours().toString().padStart(2, "0")}:${diff
            .minutes()
            .toString()
            .padStart(2, "0")}:${diff.seconds().toString().padStart(2, "0")}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation logic for required fields
        if (!shiftName.trim()) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Shift Name is required.',
                life: 5000,
            });
            return;
        }

        if (!shiftType) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Shift Type is required.',
                life: 5000,
            });
            return;
        }

        if (!startTime || !endTime) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Start Time and End Time are required.',
                life: 5000,
            });
            return;
        }

        if (dayjs(endTime).isBefore(dayjs(startTime))) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'End Time must be after Start Time.',
                life: 5000,
            });
            return;
        }

        // if (!workingHours || workingHours <= 0) {
        //     toast.current.show({
        //         severity: 'error',
        //         summary: 'Error',
        //         detail: 'Working Hours must be greater than 0.',
        //         life: 5000,
        //     });
        //     return;
        // }

        if (shiftType === "Flexible" && !cutOffTime) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Cut-Off Time is required for Flexible shifts.',
                life: 5000,
            });
            return;
        }

        if (!cutOffLeaveType) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Cut-Off Leave Type is required.',
                life: 5000,
            });
            return;
        }

        if (!effectiveFrom) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Effective From date is required.',
                life: 5000,
            });
            return;
        }

        // Prepare the data object with additional fields
        const data = {
            shiftline: {
                shift_name: shiftName,
                shift_type: shiftType,
                start_time: startTime.format("HH:mm:ss"),
                end_time: endTime.format("HH:mm:ss"),
                working_hours: formatDuration(workingHours),
                cut_off_time: shiftType === "Flexible" ? cutOffTime.format("HH:mm:ss") : null,
                leave_type: cutOffLeaveType,
                effective_from: effectiveFrom.format("YYYY-MM-DD"),
                priority: priority,
                repeat_days: repeatDays ? selectedRepeatDays.map(day => day.value) : [],
                specific_date: !repeatDays ? specificDate.format("YYYY-MM-DD") : null,
                shift: parseInt(id),
                transition_time: transitionTime.format("HH:mm:ss"),
            },
            breaks: breaks.map((b) => {
                if (b.type === "Flexible") {
                    return {
                        break_name: b.name,
                        break_type: b.type,
                        break_duration: formatDuration(b.break_duration),
                        start_time: null,
                        end_time: null,
                    };
                }
                return {
                    break_name: b.name,
                    break_type: b.type,
                    break_duration: calculateDuration(b.startTime, b.endTime),
                    start_time: b.startTime.format("HH:mm:ss"),
                    end_time: b.endTime.format("HH:mm:ss"),
                };
            }),
        };

        try {
            const token = localStorage.getItem("accessToken");
            if (!token) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Authentication token not found. Please log in.',
                    life: 5000,
                });
                return;
            }

            console.log("Sending data:", JSON.stringify(data));

            const response = await axios.post(
                `${process.env.REACT_APP_API}/api/employee/create-shiftline/`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            console.log("API Response:", response.data);
            toast.current.show({
                severity: 'success',
                summary: 'Success',
                detail: 'New Sub Shift Added Successfully!',
                life: 5000,
            });
            resetForm();
        } catch (error) {
            console.error("API Submission Error:", error.response ? error.response.data : error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: `Failed to save shift: ${error.response?.data?.message || error.message}`,
                life: 5000,
            });
        }
    };

    const resetForm = () => {
        setShiftName("");
        setShiftType("");
        setPriority(1);
        setCutOffTime(dayjs());
        setCutOffLeaveType("");
        setStartTime(dayjs());
        setEndTime(dayjs());
        setWorkingHours(1);
        setEffectiveFrom(dayjs());
        setBreaks([]);
        setRepeatDays(false);
        setSelectedRepeatDays([]);
        setSpecificDate(dayjs());
    };

    return (
        <div className="shift-form p-3">
            <Toast ref={toast} position="bottom-right" />
            <h4 className='mb-3'>Subshift Add</h4>
            <form className="d-flex flex-wrap gap-3" onSubmit={handleSubmit}>
                <TextField
                    label="Enter Shift Name"
                    variant="outlined"
                    fullWidth
                    style={{ maxWidth: "48%", flex: "1 1 48%" }}
                    value={shiftName}
                    onChange={(e) => setShiftName(e.target.value)}
                />
                <FormControl fullWidth style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                    <InputLabel>Shift Type</InputLabel>
                    <Select
                        value={shiftType}
                        onChange={(e) => setShiftType(e.target.value)}
                    >
                        <MenuItem value="Flexible">Flexible</MenuItem>
                        <MenuItem value="Fixed">Fixed</MenuItem>
                    </Select>
                </FormControl>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <TimePicker
                            label="Start Time"
                            value={startTime}
                            onChange={(newValue) => setStartTime(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </div>
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <TimePicker
                            label="End Time"
                            value={endTime}
                            onChange={(newValue) => setEndTime(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </div>
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <TextField
                            label="Working Hours"
                            variant="outlined"
                            fullWidth
                            type="number"
                            value={workingHours}
                            onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 0) setWorkingHours(value);
                            }}
                            inputProps={{ min: 0 }}
                        />
                    </div>
                    <FormControl fullWidth style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <InputLabel>Cut-Off Leave Type</InputLabel>
                        <Select
                            value={cutOffLeaveType}
                            onChange={(e) => setCutOffLeaveType(e.target.value)}
                        >
                            <MenuItem value="Quarter">Quarter</MenuItem>
                            <MenuItem value="Half">Half</MenuItem>
                        </Select>
                    </FormControl>
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <TimePicker
                            label="Cut-Off Time"
                            value={cutOffTime}
                            onChange={(newValue) => setTransitionTime(newValue)}
                            disabled={shiftType !== "Flexible"}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </div>
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <DatePicker
                            label="Effective From"
                            value={effectiveFrom}
                            onChange={(newValue) => setEffectiveFrom(newValue)}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </div>
                </LocalizationProvider>
                <TextField
                    label="Priority"
                    type="number"
                    variant="outlined"
                    fullWidth
                    style={{ maxWidth: "48%", flex: "1 1 48%" }}
                    value={priority}
                    onChange={(e) => setPriority(Number(e.target.value) || 1)}
                    inputProps={{ min: 1 }}
                />
                {repeatDays ? (
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <ReactSelect
                            options={repeatOptions}
                            isMulti
                            value={selectedRepeatDays}
                            onChange={(selected) => setSelectedRepeatDays(selected)}
                            placeholder="Select repeat days"
                            styles={{
                                container: (provided) => ({
                                    ...provided,
                                    width: "100%",
                                }),
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Specific Date"
                                value={specificDate}
                                onChange={(newValue) => setSpecificDate(newValue)}
                                slotProps={{ textField: { fullWidth: true } }}
                            />
                        </LocalizationProvider>
                    </div>
                )}
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div style={{ maxWidth: "48%", flex: "1 1 48%" }}>
                        <TimePicker
                            label="Transition Time"
                            value={transitionTime}
                            onChange={(newValue) => setCutOffTime(newValue)}
                            disabled={shiftType !== "Flexible"}
                            slotProps={{ textField: { fullWidth: true } }}
                        />
                    </div>
                </LocalizationProvider>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={repeatDays}
                            onChange={(e) => setRepeatDays(e.target.checked)}
                        />
                    }
                    label="Repeat Days"
                    style={{ flex: "1 1 48%",maxWidth:'48%' }}
                />

                <div className="break-section w-100 d-flex flex-column justify-content-end">
                    <button
                        className="btn btn-outline-success"
                        data-bs-toggle="modal"
                        data-bs-target="#breakModal"
                        type="button"
                    >
                        Add Break <IconPlus stroke={2} />
                    </button>
                    <div className="break-preview mt-3">
                        {breaks.map((b, index) => (
                            <div
                                key={index}
                                className="break-item p-3 d-flex flex-column border rounded shadow-sm"
                            >
                                <div className="d-flex justify-content-between">
                                    <strong>{b.name} - {b.type}</strong>
                                    <Button
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        onClick={() => handleRemoveBreak(index)}
                                    >
                                        <IconTrash stroke={1} />
                                    </Button>
                                </div>
                                <p className="mb-0">
                                    <span>Duration:</span>{" "}
                                    {b.type === "Flexible"
                                        ? formatDuration(b.break_duration)
                                        : calculateDuration(b.startTime, b.endTime)}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="d-flex w-100 justify-content-end">
                    <button type="submit" className="btn btn-primary">
                        Save Shift <IconCheck stroke={2} />
                    </button>
                </div>
            </form>
            <div
                className="modal fade"
                id="breakModal"
                tabIndex="-1"
                aria-labelledby="breakModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="breakModalLabel">
                                Add Break
                            </h5>
                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                            ></button>
                        </div>
                        <div className="modal-body d-flex flex-column gap-3">
                            <TextField
                                label="Break Name"
                                variant="outlined"
                                fullWidth
                                value={newBreak.name}
                                onChange={(e) =>
                                    setNewBreak({ ...newBreak, name: e.target.value })
                                }
                            />
                            <FormControl fullWidth>
                                <InputLabel>Break Type</InputLabel>
                                <Select
                                    value={newBreak.type}
                                    onChange={(e) => setNewBreak({ ...newBreak, type: e.target.value })}
                                >
                                    <MenuItem value="Fixed">Fixed</MenuItem>
                                    <MenuItem value="Flexible">Flexible</MenuItem>
                                </Select>
                            </FormControl>
                            {newBreak.type === "Flexible" ? (
                                <TextField
                                    label="Break Duration (Hours)"
                                    variant="outlined"
                                    fullWidth
                                    type="number"
                                    value={newBreak.break_duration}
                                    onChange={(e) =>
                                        setNewBreak({
                                            ...newBreak,
                                            break_duration: Number(e.target.value),
                                        })
                                    }
                                />
                            ) : (
                                <>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <TimePicker
                                            label="Start Time"
                                            value={newBreak.startTime}
                                            onChange={(newValue) =>
                                                setNewBreak({ ...newBreak, startTime: newValue })
                                            }
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                        <TimePicker
                                            label="End Time"
                                            value={newBreak.endTime}
                                            onChange={(newValue) =>
                                                setNewBreak({ ...newBreak, endTime: newValue })
                                            }
                                            slotProps={{ textField: { fullWidth: true } }}
                                        />
                                    </LocalizationProvider>
                                    <TextField
                                        label="Duration (Read Only)"
                                        variant="outlined"
                                        fullWidth
                                        value={calculateDuration(newBreak.startTime, newBreak.endTime)}
                                        InputProps={{ readOnly: true }}
                                    />
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                                id="closeBreakModal"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleAddBreak}
                            >
                                Save Break
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}